import { AuthState, User, Company, UserRole } from '../types/auth';
import { secureStorage } from '../utils/secureStorage';

const STORAGE_KEYS = {
  AUTH_STATE: 'infrasec_auth_state',
  ACCESS_TOKEN: 'infrasec_access_token',
  USER_DATA: 'infrasec_user_data',
  COMPANY_DATA: 'infrasec_company_data',
} as const;

export const authStorage = {
  saveAuthState: (authData: {
    accessToken: string;
    expiresIn: number;
    user: User;
    company?: Company;
  }): void => {
    const expiresAt = Date.now() + (authData.expiresIn * 1000);
    
    const authState: AuthState = {
      isAuthenticated: true,
      user: authData.user,
      company: authData.company || null,
      accessToken: authData.accessToken,
      expiresAt,
    };

    // Store complete auth state
    secureStorage.setItem(STORAGE_KEYS.AUTH_STATE, authState);
    
    // Store individual components for easier access
    secureStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, authData.accessToken);
    secureStorage.setItem(STORAGE_KEYS.USER_DATA, authData.user);
    if (authData.company) {
      secureStorage.setItem(STORAGE_KEYS.COMPANY_DATA, authData.company);
    }
  },

  getAuthState: (): AuthState | null => {
    const authState = secureStorage.getItem(STORAGE_KEYS.AUTH_STATE);
    
    if (!authState) {
      return null;
    }

    // Check if token is expired
    if (authState.expiresAt && Date.now() > authState.expiresAt) {
      authStorage.clearAuthState();
      return null;
    }

    return authState;
  },

  getAccessToken: (): string | null => {
    const authState = authStorage.getAuthState();
    return authState?.accessToken || null;
  },

  getUserData: (): User | null => {
    const authState = authStorage.getAuthState();
    return authState?.user || null;
  },

  getCompanyData: (): Company | null => {
    const authState = authStorage.getAuthState();
    return authState?.company || null;
  },

  isAuthenticated: (): boolean => {
    const authState = authStorage.getAuthState();
    return authState?.isAuthenticated || false;
  },

  getUserRole: (): UserRole => {
    const user = authStorage.getUserData();
    if (!user) return 'user';
    
    // userType 1 = admin, userType 2 = company user
    // If companyId is null, it's an admin user
    return user.companyId === null ? 'admin' : 'user';
  },

  clearAuthState: (): void => {
    secureStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
    secureStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    secureStorage.removeItem(STORAGE_KEYS.USER_DATA);
    secureStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
  },

  isTokenExpired: (): boolean => {
    const authState = authStorage.getAuthState();
    if (!authState?.expiresAt) return true;
    
    return Date.now() > authState.expiresAt;
  },
};
